/*
 * PLATFORM ASSESSMENT URL FILTERS HOOK
 * 
 * PURPOSE:
 * Manages URL query parameters for platform assessment averages page
 * 
 * WHY SEPARATE FROM PEARLI:
 * - Keeps platform state isolated
 * - No coupling between free (Pearli) and platform flows
 * - Different URL structure may be needed in future
 * - Independent maintenance and testing
 * 
 * USAGE:
 * const { params, setParams, viewMode, filters, setFilter, clearFilters } = useAssessmentUrlFilters();
 */

import { useSearchParams } from "react-router-dom";

export default function useAssessmentUrlFilters() {
    const [params, setParams] = useSearchParams();

    // View mode: "current" (default) or "range"
    const viewMode = params.get("view") || "current";

    // Extract filters from URL
    const filters = {
        age: params.get("age") || "",
        gender: params.get("gender") || "",
        location: params.get("location") || "",
        description: params.get("description") || "",
        jobTitle: params.get("job_title") || "",
        company: params.get("company") || "",
        interest: params.get("interest") || "",
    };

    /**
     * Set a single filter value
     * @param {string} key - Filter key (camelCase)
     * @param {string} value - Filter value (empty string to remove)
     */
    const setFilter = (key, value) => {
        const p = new URLSearchParams(params);
        
        // Convert camelCase to snake_case for URL
        const urlKey = key === "jobTitle" ? "job_title" : key;
        
        if (value) {
            p.set(urlKey, value);
        } else {
            p.delete(urlKey);
        }
        
        setParams(p);
    };

    /**
     * Clear all filters from URL
     */
    const clearFilters = () => {
        const p = new URLSearchParams(params);
        
        // Remove all filter keys
        ["age", "gender", "location", "description", "job_title", "company", "interest"]
            .forEach((k) => p.delete(k));
        
        setParams(p);
    };

    return { 
        params, 
        setParams, 
        viewMode, 
        filters, 
        setFilter, 
        clearFilters 
    };
}
